import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DollarSign, Users, FileText, TrendingDown, Search, X,
  AlertTriangle, Eye, RefreshCw, Loader, Building2, User, Phone,
  CreditCard, CheckCircle, SlidersHorizontal, Eraser, RotateCcw, History
} from 'lucide-react';
import {
  getClients, getClientDettes, payerDetteClient,
  ajusterDetteClient, getAjustementsDette, revoquerAjustementDette,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import './Dettes.css';

const fmt = (n) => new Intl.NumberFormat('fr-FR').format(n || 0) + ' XOF';

// Date du jour au format YYYY-MM-DD (heure locale) pour <input type="date">
const aujourdhui = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const MODES = [
  { value: 'especes',       label: 'Espèces' },
  { value: 'wave',          label: 'Wave' },
  { value: 'orange_money',  label: 'Orange Money' },
  { value: 'carte_bancaire',label: 'Carte bancaire' },
  { value: 'cheque',        label: 'Chèque' },
  { value: 'avoir',         label: 'Avoir client' },
  { value: 'autre',         label: 'Autre' },
];

export default function Dettes() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Réduire une créance sans encaissement : réservé aux administrateurs
  const peutAjuster = ['admin-entreprise', 'admin-pro', 'super-admin'].includes(user?.role);

  const [clients,   setClients]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState('');

  // Modal acompte (règlement global FIFO)
  const [modal,       setModal]       = useState(null);  // { client }
  const [factures,    setFactures]    = useState([]);    // factures impayées (triées + ancienne → + récente)
  const [loadFact,    setLoadFact]    = useState(false);
  const [montant,     setMontant]     = useState('');
  const [modePaie,    setModePaie]    = useState('especes');
  const [commentaire, setCommentaire] = useState('');
  const [datePaie,    setDatePaie]    = useState(aujourdhui());
  const [submitting,  setSubmitting]  = useState(false);
  const [success,     setSuccess]     = useState(null);
  const [errModal,    setErrModal]    = useState('');

  // Modal réajustement / effacement de dette
  const [adjModal,   setAdjModal]   = useState(null); // { client }
  const [adjNouveau, setAdjNouveau] = useState('');
  const [adjMotif,   setAdjMotif]   = useState('');
  const [adjComm,    setAdjComm]    = useState('');
  const [adjBusy,    setAdjBusy]    = useState(false);
  const [adjErr,     setAdjErr]     = useState('');
  const [adjSuccess, setAdjSuccess] = useState(null);
  const [adjHist,    setAdjHist]    = useState([]);
  const [adjHistLoad,setAdjHistLoad]= useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getClients({ avecDette: 'true' });
      setClients(res.data.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const detteTotal   = clients.reduce((s, c) => s + (c.detteActuelle || 0), 0);
  const nbFactures   = clients.reduce((s, c) => s + (c.nbFacturesImpayees || 0), 0);
  const detteMoyenne = clients.length ? detteTotal / clients.length : 0;

  const filtered = clients.filter(c =>
    c.nom.toLowerCase().includes(search.toLowerCase()) ||
    (c.telephone || '').includes(search)
  );

  const detteColor = (c) => {
    const d = c.detteActuelle || 0;
    if (!d) return '#16a34a';
    if (!c.limiteCredit) return '#3b82f6';
    const pct = (d / c.limiteCredit) * 100;
    return pct >= 80 ? '#dc2626' : pct >= 50 ? '#d97706' : '#3b82f6';
  };

  const detteBarColor = (c) => {
    if (!c.limiteCredit) return '#3b82f6';
    const pct = (c.detteActuelle / c.limiteCredit) * 100;
    return pct >= 80 ? '#dc2626' : pct >= 50 ? '#d97706' : '#3b82f6';
  };

  // ── Ouvrir modal ──
  const openModal = async (e, client) => {
    e.stopPropagation();
    setModal({ client });
    setFactures([]); setMontant('');
    setModePaie('especes'); setCommentaire(''); setDatePaie(aujourdhui()); setErrModal(''); setSuccess(null);
    setLoadFact(true);
    try {
      const res = await getClientDettes(client._id);
      const ventes = res.data.data || [];
      // Trier de la plus ancienne à la plus récente (FIFO)
      ventes.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      setFactures(ventes);
    } catch { setErrModal('Impossible de charger les factures.'); }
    finally { setLoadFact(false); }
  };

  // Reste dû sur une facture : hors encaissements ET hors part annulée
  const resteDu = (f) =>
    Math.max(0, (f.totalTTC || 0) - (f.montantPaye || 0) - (f.montantAnnule || 0));

  // Total restant à payer pour ce client
  const detteTotale = () => factures.reduce((s, f) => s + resteDu(f), 0);

  // Aperçu de la répartition FIFO selon le montant saisi
  const allocationPreview = () => {
    const m = parseFloat(montant) || 0;
    let restant = m;
    return factures.map(f => {
      const resteF = resteDu(f);
      const alloc = Math.min(resteF, restant);
      restant -= alloc;
      return { facture: f, reste: resteF, allocation: alloc };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrModal('');
    const m = parseFloat(montant);
    if (!m || m <= 0) { setErrModal('Montant invalide.'); return; }
    const total = detteTotale();
    if (m > total) { setErrModal(`Le montant dépasse la dette totale (${fmt(total)}).`); return; }
    setSubmitting(true);
    try {
      const res = await payerDetteClient(modal.client._id, {
        montant: m,
        modePaiement: modePaie,
        commentaire: commentaire || undefined,
        datePaiement: datePaie || undefined,
      });
      setSuccess({
        montant: m,
        mode: modePaie,
        client: modal.client.nom,
        date: datePaie,
        allocations: res.data.data?.allocations || [],
      });
      load();
    } catch (err) {
      setErrModal(err.response?.data?.message || 'Erreur lors de l\'enregistrement.');
    } finally { setSubmitting(false); }
  };

  const closeModal = () => { setModal(null); setSuccess(null); };

  // ── Réajustement / effacement de dette ──
  const chargerHistorique = async (clientId) => {
    setAdjHistLoad(true);
    try {
      const res = await getAjustementsDette(clientId);
      setAdjHist(res.data.data || []);
    } catch { setAdjHist([]); }
    finally { setAdjHistLoad(false); }
  };

  const openAdj = async (e, client, effacerDirect = false) => {
    e.stopPropagation();
    setAdjModal({ client });
    setAdjNouveau(effacerDirect ? '0' : '');
    setAdjMotif(''); setAdjComm(''); setAdjErr(''); setAdjSuccess(null);
    setAdjHist([]);
    await chargerHistorique(client._id);
  };

  const closeAdj = () => { setAdjModal(null); setAdjSuccess(null); setAdjErr(''); };

  const adjDette   = adjModal?.client?.detteActuelle || 0;
  const adjCible   = adjNouveau === '' ? null : Number(adjNouveau);
  const adjRetrait = adjCible === null || !Number.isFinite(adjCible)
    ? 0
    : Math.max(0, adjDette - adjCible);

  const submitAdj = async (e) => {
    e.preventDefault();
    setAdjErr('');
    if (adjCible === null || !Number.isFinite(adjCible) || adjCible < 0) {
      setAdjErr('Saisissez le nouveau montant de la dette.'); return;
    }
    if (adjCible > adjDette) {
      setAdjErr(`Le nouveau montant ne peut pas dépasser la dette actuelle (${fmt(adjDette)}). Pour augmenter une dette, créez une vente à crédit.`);
      return;
    }
    if (adjRetrait <= 0) { setAdjErr('Le nouveau montant est identique à la dette actuelle.'); return; }
    if (!adjMotif.trim()) { setAdjErr('Le motif est obligatoire.'); return; }

    setAdjBusy(true);
    try {
      const res = await ajusterDetteClient(adjModal.client._id, {
        nouveauMontant: adjCible,
        motif: adjMotif.trim(),
        commentaire: adjComm.trim() || undefined,
      });
      setAdjSuccess(res.data.data);
      load();
      chargerHistorique(adjModal.client._id);
    } catch (err) {
      setAdjErr(err.response?.data?.message || "Erreur lors de l'ajustement.");
    } finally { setAdjBusy(false); }
  };

  const revoquer = async (lotId) => {
    if (!window.confirm('Révoquer cet ajustement ? Le montant sera remis en dette.')) return;
    setAdjBusy(true);
    try {
      await revoquerAjustementDette(lotId);
      load();
      await chargerHistorique(adjModal.client._id);
      setAdjErr('');
    } catch (err) {
      setAdjErr(err.response?.data?.message || 'Erreur lors de la révocation.');
    } finally { setAdjBusy(false); }
  };

  if (loading) return (
    <div className="dt-loading"><Loader className="dt-spinner" size={26}/><span>Chargement…</span></div>
  );

  return (
    <div className="dt-page">

      {/* ── Header ── */}
      <div className="dt-header">
        <div className="dt-header-left">
          <div className="dt-header-icon"><DollarSign size={18}/></div>
          <div>
            <h1 className="dt-header-title">Dettes clients</h1>
            <p className="dt-header-sub">{clients.length} client{clients.length > 1 ? 's' : ''} avec encours</p>
          </div>
        </div>
        <button className="dt-refresh-btn" onClick={load}>
          <RefreshCw size={14}/> Actualiser
        </button>
      </div>

      <div className="dt-content">

        {/* KPI */}
        <div className="dt-kpi-row">
          {[
            { label:'Dette totale',     value: fmt(detteTotal),   color:'#dc2626', bg:'#fee2e2', icon: DollarSign   },
            { label:'Clients endettés', value: clients.length,    color:'#6366f1', bg:'#eef2ff', icon: Users        },
            { label:'Factures impayées',value: nbFactures,        color:'#d97706', bg:'#fef3c7', icon: FileText     },
            { label:'Dette moyenne',    value: fmt(detteMoyenne), color:'#3b82f6', bg:'#eff6ff', icon: TrendingDown },
          ].map((k, i) => {
            const Icon = k.icon;
            return (
              <div key={i} className="dt-kpi" style={{ borderTopColor: k.color }}>
                <div className="dt-kpi-icon" style={{ background: k.bg, color: k.color }}><Icon size={14}/></div>
                <div className="dt-kpi-val">{k.value}</div>
                <div className="dt-kpi-label">{k.label}</div>
              </div>
            );
          })}
        </div>

        {detteTotal > 100000 && (
          <div className="dt-alert">
            <AlertTriangle size={15}/>
            <span><strong>Attention :</strong> le total des dettes dépasse {fmt(100000)}.</span>
          </div>
        )}

        {/* Filters */}
        <div className="dt-filters">
          <div className="dt-search-wrap">
            <Search className="dt-search-icon" size={13}/>
            <input className="dt-search" placeholder="Rechercher par nom ou téléphone…"
              value={search} onChange={e => setSearch(e.target.value)}/>
            {search && <button className="dt-search-clear" onClick={() => setSearch('')}><X size={12}/></button>}
          </div>
          <span className="dt-count">{filtered.length} résultat{filtered.length > 1 ? 's' : ''}</span>
        </div>

        {/* Table */}
        {filtered.length === 0 ? (
          <div className="dt-empty">
            <DollarSign size={28}/>
            <span>{search ? 'Aucun client trouvé' : 'Aucune dette en cours'}</span>
          </div>
        ) : (
          <div className="dt-card">
            <table className="dt-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Téléphone</th>
                  <th>Dette actuelle</th>
                  <th>Limite crédit</th>
                  <th>Utilisation</th>
                  <th>Factures</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => {
                  const dette  = c.detteActuelle || 0;
                  const limite = c.limiteCredit;
                  const pct    = limite ? Math.min((dette / limite) * 100, 100) : null;
                  const color  = detteColor(c);
                  const barCol = detteBarColor(c);
                  const isEnt  = c.type === 'entreprise';

                  return (
                    <tr key={c._id} onClick={() => navigate(`/clients/${c._id}`)} className="dt-row-link">
                      <td>
                        <div className="dt-client-cell">
                          <div className={`dt-av ${isEnt ? 'dt-av--ent' : ''}`}>
                            {isEnt ? <Building2 size={14}/> : <User size={14}/>}
                          </div>
                          <div>
                            <div className="dt-client-name">{c.nom}</div>
                            {isEnt && <div className="dt-client-type">Entreprise</div>}
                          </div>
                        </div>
                      </td>
                      <td>
                        {c.telephone
                          ? <span className="dt-phone"><Phone size={11}/> {c.telephone}</span>
                          : <span className="dt-muted">—</span>}
                      </td>
                      <td>
                        <span className="dt-dette-badge" style={{ background: color + '1a', color }}>
                          {fmt(dette)}
                        </span>
                      </td>
                      <td>
                        {limite != null
                          ? <span className="dt-limite">{fmt(limite)}</span>
                          : <span className="dt-illimite">Illimité</span>}
                      </td>
                      <td>
                        {pct !== null ? (
                          <div className="dt-bar-wrap">
                            <div className="dt-bar-track">
                              <div className="dt-bar-fill" style={{ width:`${pct}%`, background: barCol }}/>
                            </div>
                            <span className="dt-bar-pct" style={{ color: barCol }}>{Math.round(pct)}%</span>
                          </div>
                        ) : <span className="dt-muted">—</span>}
                      </td>
                      <td>
                        <span className="dt-factures-badge">{c.nbFacturesImpayees || 0}</span>
                      </td>
                      <td onClick={e => e.stopPropagation()} className="dt-actions-cell">
                        <button className="dt-btn-acompte" onClick={e => openModal(e, c)}>
                          <CreditCard size={13}/> Acompte
                        </button>
                        {peutAjuster && (
                          <button className="dt-btn-ajuster" title="Réajuster ou effacer cette dette"
                            onClick={e => openAdj(e, c)}>
                            <SlidersHorizontal size={13}/> Ajuster
                          </button>
                        )}
                        <button className="dt-btn-view" onClick={() => navigate(`/clients/${c._id}`)}>
                          <Eye size={13}/> Détails
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════
          Modal acompte
         ══════════════════════════════════════ */}
      {modal && (
        <>
          <div className="dt-overlay" onClick={closeModal}/>
          <div className="dt-modal">
            <div className="dt-modal-head">
              <div className="dt-modal-head-left">
                <div className="dt-modal-ico"><CreditCard size={15}/></div>
                <div>
                  <h2 className="dt-modal-title">Enregistrer un acompte</h2>
                  <p className="dt-modal-sub">{modal.client.nom}</p>
                </div>
              </div>
              <button className="dt-modal-close" onClick={closeModal}><X size={16}/></button>
            </div>

            {success ? (
              <div className="dt-modal-success">
                <CheckCircle size={40} className="dt-success-ico"/>
                <p className="dt-success-title">Acompte enregistré</p>
                <p className="dt-success-sub">
                  <strong>{fmt(success.montant)}</strong> encaissé pour <strong>{success.client}</strong>
                  {success.date && <> le <strong>{success.date.split('-').reverse().join('/')}</strong></>}
                </p>
                <div className="dt-modal-footer">
                  <button className="dt-btn-primary" onClick={closeModal}>Fermer</button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="dt-modal-body">

                  {loadFact ? (
                    <div className="dt-modal-loading"><Loader size={18} className="dt-spinner"/> Chargement des factures…</div>
                  ) : factures.length === 0 ? (
                    <div className="dt-modal-empty">Aucune facture impayée trouvée.</div>
                  ) : (() => {
                    const avoir = Number(modal.client.avoirDisponible) || 0;
                    const isAvoirMode = modePaie === 'avoir';
                    const maxPayable = isAvoirMode ? Math.min(detteTotale(), avoir) : detteTotale();
                    return (
                    <>
                      {/* Récap dette totale + avoir */}
                      <div className="dt-facture-recap">
                        <div className="dt-recap-row">
                          <span>Dette totale</span><strong className="dt-reste">{fmt(detteTotale())}</strong>
                        </div>
                        <div className="dt-recap-row">
                          <span>Factures impayées</span><strong>{factures.length}</strong>
                        </div>
                        {avoir > 0 && (
                          <div className="dt-recap-row" style={{ background: '#dcfce7', color: '#15803d' }}>
                            <span>💳 Avoir disponible</span><strong>{fmt(avoir)}</strong>
                          </div>
                        )}
                        <div className="dt-recap-row dt-recap-row--reste">
                          <span>Plus ancienne</span>
                          <strong>{factures[0]?.numeroFacture} · {new Date(factures[0]?.createdAt).toLocaleDateString('fr-FR')}</strong>
                        </div>
                      </div>

                      {/* Montant à régler */}
                      <div className="dt-field">
                        <label>Montant à régler (XOF) *</label>
                        <input
                          type="number" min="1" max={maxPayable} step="1"
                          value={montant} onChange={e => setMontant(e.target.value)}
                          placeholder={`Max : ${fmt(maxPayable)}`}
                          required
                        />
                        <span className="dt-field-hint">
                          {isAvoirMode
                            ? <>Paiement par <strong>avoir client</strong> — max {fmt(avoir)} disponible.</>
                            : <>Le montant sera imputé automatiquement sur les factures, <strong>de la plus ancienne à la plus récente</strong>.</>}
                        </span>
                      </div>

                      {/* Aperçu de la répartition FIFO */}
                      {parseFloat(montant) > 0 && (
                        <div className="dt-field">
                          <label>Répartition prévue</label>
                          <div className="dt-fifo-preview">
                            {allocationPreview().map(({ facture, reste, allocation }, i) => {
                              if (allocation <= 0 && i > 0) return null;
                              const resteApres = reste - allocation;
                              const soldee    = allocation > 0 && resteApres === 0;
                              return (
                                <div key={facture._id} className={`dt-fifo-line${allocation > 0 ? '' : ' dt-fifo-line--off'}`}>
                                  <div className="dt-fifo-num">
                                    <span className="dt-fifo-numero">{facture.numeroFacture}</span>
                                    <span className="dt-fifo-date">{new Date(facture.createdAt).toLocaleDateString('fr-FR')}</span>
                                  </div>
                                  <div className="dt-fifo-amounts">
                                    {allocation > 0 ? (
                                      <>
                                        <span className="dt-fifo-alloc">−{fmt(allocation)}</span>
                                        <span className="dt-fifo-after">
                                          {soldee ? <em className="dt-fifo-soldee">Soldée ✓</em> : <>Reste : {fmt(resteApres)}</>}
                                        </span>
                                      </>
                                    ) : (
                                      <span className="dt-fifo-skip">Non touchée</span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Mode paiement */}
                      <div className="dt-field">
                        <label>Mode de paiement</label>
                        <div className="dt-mode-grid">
                          {MODES.map(m => (
                            <button key={m.value} type="button"
                              className={`dt-mode-btn ${modePaie === m.value ? 'dt-mode-btn--active' : ''}`}
                              onClick={() => setModePaie(m.value)}>
                              {m.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Date de l'acompte */}
                      <div className="dt-field">
                        <label>Date de l'acompte</label>
                        <input type="date" required value={datePaie} max={aujourdhui()}
                          onChange={e => setDatePaie(e.target.value)}/>
                      </div>

                      {/* Commentaire */}
                      <div className="dt-field">
                        <label>Commentaire <span className="dt-opt">(optionnel)</span></label>
                        <input type="text" value={commentaire}
                          onChange={e => setCommentaire(e.target.value)}
                          placeholder="Référence, note…"/>
                      </div>

                      {errModal && (
                        <div className="dt-modal-err"><AlertTriangle size={13}/> {errModal}</div>
                      )}
                    </>
                    );
                  })()}
                </div>

                {!loadFact && factures.length > 0 && (
                  <div className="dt-modal-footer">
                    <button type="button" className="dt-btn-cancel" onClick={closeModal}>Annuler</button>
                    <button type="submit" className="dt-btn-primary" disabled={submitting || !montant}>
                      {submitting
                        ? <><Loader size={13} className="dt-spinner"/> Enregistrement…</>
                        : <><CreditCard size={13}/> Encaisser {montant ? fmt(parseFloat(montant)||0) : ''}</>}
                    </button>
                  </div>
                )}
              </form>
            )}
          </div>
        </>
      )}

      {/* ══════════════════════════════════════
          Modal réajustement / effacement de dette
         ══════════════════════════════════════ */}
      {adjModal && (
        <>
          <div className="dt-overlay" onClick={closeAdj}/>
          <div className="dt-modal">
            <div className="dt-modal-head">
              <div className="dt-modal-head-left">
                <div className="dt-modal-ico dt-modal-ico--adj"><SlidersHorizontal size={15}/></div>
                <div>
                  <h2 className="dt-modal-title">Réajuster la dette</h2>
                  <p className="dt-modal-sub">{adjModal.client.nom}</p>
                </div>
              </div>
              <button className="dt-modal-close" onClick={closeAdj}><X size={16}/></button>
            </div>

            {adjSuccess ? (
              <div className="dt-modal-success">
                <CheckCircle size={40} className="dt-success-ico"/>
                <p className="dt-success-title">
                  {adjSuccess.type === 'annulation' ? 'Dette effacée' : 'Dette réajustée'}
                </p>
                <p className="dt-success-sub">
                  <strong>{fmt(adjSuccess.montantRetire)}</strong> retiré(s) sur{' '}
                  {adjSuccess.lignes?.length || 0} facture(s) — nouvelle dette :{' '}
                  <strong>{fmt(adjSuccess.detteApres)}</strong>
                </p>
                <div className="dt-modal-footer">
                  <button className="dt-btn-primary" onClick={closeAdj}>Fermer</button>
                </div>
              </div>
            ) : (
              <form onSubmit={submitAdj}>
                <div className="dt-modal-body">

                  <div className="dt-adj-current">
                    <span>Dette actuelle</span>
                    <strong>{fmt(adjDette)}</strong>
                  </div>

                  <div className="dt-modal-field">
                    <label>Nouveau montant de la dette *</label>
                    <div className="dt-adj-input-row">
                      <input
                        type="number" min="0" max={adjDette} step="1"
                        value={adjNouveau}
                        onChange={e => setAdjNouveau(e.target.value)}
                        placeholder="0"
                        autoFocus/>
                      <button type="button" className="dt-btn-effacer"
                        title="Ramener la dette à zéro"
                        onClick={() => setAdjNouveau('0')}>
                        <Eraser size={13}/> Effacer tout
                      </button>
                    </div>
                  </div>

                  {adjRetrait > 0 && (
                    <div className="dt-adj-preview">
                      <TrendingDown size={13}/>
                      <span>
                        <strong>{fmt(adjRetrait)}</strong> seront retirés de la dette, imputés sur les
                        factures les plus anciennes d'abord. Ce montant n'est <strong>pas</strong> compté
                        comme un encaissement.
                      </span>
                    </div>
                  )}

                  <div className="dt-modal-field">
                    <label>Motif *</label>
                    <input type="text" value={adjMotif}
                      onChange={e => setAdjMotif(e.target.value)}
                      placeholder="Erreur de saisie, geste commercial, créance irrécouvrable…"/>
                  </div>

                  <div className="dt-modal-field">
                    <label>Commentaire</label>
                    <input type="text" value={adjComm}
                      onChange={e => setAdjComm(e.target.value)}
                      placeholder="Précision facultative"/>
                  </div>

                  {adjErr && (
                    <div className="dt-modal-err"><AlertTriangle size={13}/> {adjErr}</div>
                  )}

                  {/* Historique des ajustements */}
                  <div className="dt-adj-hist">
                    <div className="dt-adj-hist-head">
                      <History size={13}/> Ajustements précédents
                      {adjHist.length > 0 && <span className="dt-factures-badge">{adjHist.length}</span>}
                    </div>
                    {adjHistLoad ? (
                      <div className="dt-muted">Chargement…</div>
                    ) : adjHist.length === 0 ? (
                      <div className="dt-muted">Aucun ajustement sur ce client.</div>
                    ) : (
                      adjHist.map(a => (
                        <div key={a.lotId} className={`dt-adj-hist-line ${a.revoque ? 'dt-adj-hist-line--off' : ''}`}>
                          <div className="dt-adj-hist-main">
                            <strong>{fmt(a.montant)}</strong>
                            <span className="dt-adj-hist-type">
                              {a.type === 'annulation' ? 'Effacement' : 'Réajustement'}
                            </span>
                            {a.revoque && <span className="dt-adj-hist-revoque">révoqué</span>}
                          </div>
                          <div className="dt-adj-hist-meta">
                            {a.motif}
                            {a.enregistrePar && ` — ${a.enregistrePar.prenom || ''} ${a.enregistrePar.nom || ''}`.trimEnd()}
                            {a.createdAt && ` — ${new Date(a.createdAt).toLocaleDateString('fr-FR')}`}
                          </div>
                          {!a.revoque && (
                            <button type="button" className="dt-btn-revoquer" disabled={adjBusy}
                              title="Remettre ce montant en dette"
                              onClick={() => revoquer(a.lotId)}>
                              <RotateCcw size={12}/> Révoquer
                            </button>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <div className="dt-modal-footer">
                  <button type="button" className="dt-btn-cancel" onClick={closeAdj}>Annuler</button>
                  <button type="submit" className="dt-btn-primary" disabled={adjBusy || adjRetrait <= 0}>
                    {adjBusy
                      ? <><Loader size={13} className="dt-spinner"/> Enregistrement…</>
                      : <><SlidersHorizontal size={13}/> Appliquer</>}
                  </button>
                </div>
              </form>
            )}
          </div>
        </>
      )}
    </div>
  );
}
